import { Button, ButtonProps } from "../button/Button";
import "./loan-card.scss";
import { IconLookup } from "@fortawesome/fontawesome-svg-core";
import { ApplicationLoanStatus } from "pages/application/applicationService";
import Badge, { BadgeColor } from "../badge/Badge";
import { formatDollarAmount } from "utilities/string";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowAltCircleRight, faCheckCircle, faClock, faPartyHorn, IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { PropertyInfo } from "pages/dashboard/home/dashboardService";
import { AMOUNT_PENDING, GENERIC_PENDING, PROPERTY_PENDING } from "utilities/Constant";

enum LoanBadgeTitles {
  New = "New",
  Started = "Started",
  Incomplete = "Incomplete",
  ApplicationReceived = "Application Received",
  Processing = "Processing",
  InitialApproval = "Initial Approval",
  ClearedToClose = "Cleared to Close",
  Closed = "Closed",
  Expired = "Expired"
}

const getBadgeDetails = (status: ApplicationLoanStatus): { badgeClass: BadgeColor; badgeTitle: string } => {
  switch (status) {
    case "new":
      return { badgeClass: "new", badgeTitle: LoanBadgeTitles.New };
    case "incomplete":
      return { badgeClass: "incomplete", badgeTitle: LoanBadgeTitles.Incomplete };
    case "started":
      return { badgeClass: "started", badgeTitle: LoanBadgeTitles.Started };
    case "processing":
      return { badgeClass: "processing", badgeTitle: LoanBadgeTitles.Processing };
    case "submittedToUnderwriting":
      return { badgeClass: "application-received", badgeTitle: LoanBadgeTitles.ApplicationReceived };
    case "conditionallyApproved":
    case "underReview":
    case "finalReview":
      return { badgeClass: "initial-approval", badgeTitle: LoanBadgeTitles.InitialApproval };
    case "clearedToClosed":
      return { badgeClass: "cleared-to-close", badgeTitle: LoanBadgeTitles.ClearedToClose };
    case "funded":
      return { badgeClass: "closed", badgeTitle: LoanBadgeTitles.Closed };
    case "expired":
      return { badgeClass: "closed", badgeTitle: LoanBadgeTitles.Expired };
  }
};

const getContextualMessageIcon = (contextualMessage: string | undefined): IconDefinition => {
  switch (contextualMessage) {
    case 'Your disclosures are ready':
      return faCheckCircle;

    case 'You have tasks pending':
      return faClock;

    case 'Congratulations!':
      return faPartyHorn;

    case 'Continue your application':
    default:
      return faArrowAltCircleRight;
  }
};

const getLoanBodyKeyValuePair = (key: string, value: string) => {
  return <div className="loanCardV2__body__keyValuePair">
    <span className="loanCardV2__body__key label-medium-regular">{key}</span>
    <span className="loanCardV2__body__value label-medium-medium">{value}</span>
  </div>;
};

const getPropertyAddress = (propertyInfo?: PropertyInfo) => {
  const streetAddress = propertyInfo?.street?.toLowerCase()?.replace("tbd", "")?.trim();
  return <>
    <p className="paragraph-medium-medium address">{streetAddress || PROPERTY_PENDING}</p>
    <p className="paragraph-medium-medium address">{propertyInfo?.city} {propertyInfo?.state}</p>
  </>;
}

interface LoanCardProps {
  property: string;
  propertyInfo?: PropertyInfo;
  loanPurpose: string;
  loanAmount: string;
  status: ApplicationLoanStatus;
  startedDate: string;
  index: number;
  loanNumber: string;
  handleLoanCardClick: (index: number) => void;
  applicationNumber: string;
  contextualMessage: string | undefined;
}

export default function LoanCardV2({
  status,
  property,
  propertyInfo,
  loanPurpose,
  loanAmount,
  startedDate,
  index,
  loanNumber,
  applicationNumber,
  handleLoanCardClick,
  contextualMessage
}: Readonly<LoanCardProps>) {
  const { badgeClass, badgeTitle } = getBadgeDetails(status);

  const buttonProps: ButtonProps = {
    label: 'Select',
    onClick: () => handleLoanCardClick(index),
    disabled: false,
    className: "loanCardV2__actions-select-loan"
  }

  const formattedLoanAmount = loanAmount?.trim().startsWith("$") ? loanAmount : formatDollarAmount(loanAmount);
  const displayAmount = formattedLoanAmount ?? AMOUNT_PENDING;

  return (
    <div className="loanCardV2">
      <div className={`loanCardV2__header`}>
        <div className={`loanCardV2__header-info`}>
          {getPropertyAddress(propertyInfo)}
          {loanNumber && <p className="paragraph-alt-small-regular loan-number">Loan #{loanNumber}</p>}
          {!loanNumber && <p className="paragraph-alt-small-regular loan-number">Application #{applicationNumber ?? GENERIC_PENDING}</p>}
        </div>
        <div className={`loanCardV2__header-status`}>
          <Badge color={badgeClass} title={badgeTitle} toUpperCase={false} />
        </div>
      </div>
      <div className={`loanCardV2__body`}>
        {getLoanBodyKeyValuePair("Started", startedDate)}
        {getLoanBodyKeyValuePair("Loan Purpose", loanPurpose)}
        {getLoanBodyKeyValuePair("Amount", displayAmount)}
      </div>
      <div className={`loanCardV2__actions`}>
        <div className="loanCardV2__actions__contextual-message label-medium-medium">
          <div className={`loanCardV2__actions__icon-wrapper ${contextualMessage === 'Congratulations!' ? 'party' : ''}`}>
            <FontAwesomeIcon icon={getContextualMessageIcon(contextualMessage) as IconLookup} />
          </div>
          {contextualMessage}
        </div>
        <Button {...buttonProps} />
      </div>
    </div>
  );
}
